/* eslint-disable react-refresh/only-export-components */
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@eigtech/ui/lib/utils'
import { LucideIcon, XIcon } from 'lucide-react'

/** Props for `Tag`. Supports variant, size, disabled, and render override. */
export interface TagProps
  extends useRender.ComponentProps<'span'>, VariantProps<typeof tagVariants> {
  /** When true, tag is non-interactive and visually muted. */
  disabled?: boolean
}

/** CVA variants for `Tag` (variant, size, disabled). Use for composition or styling consistency. */
const tagVariants = cva(
  'focus:ring-ring focus:outline-hidden inline-flex items-center justify-center rounded-sm border border-transparent font-medium focus:ring-2 focus:ring-offset-2 [&_svg]:-ms-px [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        success:
          'bg-(--color-success-accent,var(--color-green-500)) text-(--color-success-foreground,var(--color-white))',
        warning:
          'bg-(--color-warning-accent,var(--color-yellow-500)) text-(--color-warning-foreground,var(--color-white))',
        info: 'bg-(--color-info-accent,var(--color-blue-500)) text-(--color-info-foreground,var(--color-white))',
        outline: 'border-border text-secondary-foreground border bg-transparent',
        destructive: 'bg-destructive text-destructive-foreground',
      },
      disabled: {
        true: 'pointer-events-none opacity-50',
      },
      size: {
        lg: 'h-7 min-w-7 gap-1.5 px-2 text-sm [&_svg]:size-4',
        md: 'h-6 min-w-6 gap-1.5 px-[0.45rem] text-sm [&_svg]:size-4',
        sm: 'h-5 min-w-5 gap-1 px-[0.325rem] text-xs leading-3 [&_svg]:size-3.5',
        xs: 'leading-2 h-4 min-w-4 gap-1 px-1 text-xs [&_svg]:size-3.5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

/**
 * Inline chip use to note tags on a given item. Renders as a `span` by default;
 * use with `TagLabel` and optional `TagButton` for a removable tag pattern. Has `data-slot="tag"`.
 *
 * @example
 * ```tsx
 * <Tag variant="primary" size="sm">Value</Tag>
 * <Tag variant="outline" size="sm">
 *   <TagLabel>Removable</TagLabel>
 *   <TagButton onPress={onRemove} />
 * </Tag>
 * ```
 */
function Tag({
  render,
  className,
  children: childrenProp,
  variant,
  size,
  disabled,
  ...props
}: TagProps) {
  const children =
    typeof childrenProp === 'string' ? <TagLabel>{childrenProp}</TagLabel> : childrenProp

  const element = useRender({
    defaultTagName: 'span',
    render,
    props: mergeProps(
      { className: cn(tagVariants({ variant, size, disabled }), className), children },
      props
    ),
    state: {
      slot: 'tag',
    },
  })

  return element
}

/** Props for `TagLabel`. Use as the text/label child inside `Tag`. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface TagLabelProps extends useRender.ComponentProps<'span'> {}

/**
 * Label content for a `Tag`. Renders as a `span` with `line-clamp-1`. Has `data-slot="tag-label"`.
 */
function TagLabel({ render, className, ...props }: TagLabelProps) {
  const element = useRender({
    defaultTagName: 'span',
    render,
    props: mergeProps({ className: cn('line-clamp-1', className) }, props),
    state: {
      slot: 'tag-label',
    },
  })

  return element
}

/** Props for `TagButton`. Optional icon; defaults to X for remove/dismiss. */
export interface TagButtonProps extends useRender.ComponentProps<'button'> {
  /** Icon to show; defaults to `XIcon` when neither `icon` nor `children` is provided. */
  icon?: LucideIcon
}

/**
 * Optional action button inside a `Tag` (e.g. remove/dismiss). Renders as a `button` with
 * `role="button"`. Defaults to `<XIcon />` when no `children` or `icon` are passed. Has `data-slot="tag-button"`.
 */
function TagButton({ render, children, icon, className, ...props }: TagButtonProps) {
  const element = useRender({
    defaultTagName: 'button',
    render,
    props: mergeProps(
      {
        children: children || icon || <XIcon />,
        className: cn(
          '[&>svg]:opacity-100! -me-0.5 inline-flex size-4 cursor-pointer items-center justify-center rounded-md p-0 leading-none opacity-60 transition-all hover:opacity-100 [&>svg]:size-4',
          className
        ),
        role: 'button' as const,
      },
      props
    ),
    state: {
      slot: 'tag-button',
    },
  })

  return element
}

export { Tag, TagButton, TagLabel, tagVariants }
